import { useState, useEffect } from "react";
import axios from "axios";
import { Search, Car, Calendar, CheckCircle, XCircle, Loader2, LogOut, Crown, User, History, Download, Trash2, Share2, ThumbsUp, ThumbsDown, Camera, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import CameraScanner from "@/components/CameraScanner";
import ChatWidget from "@/components/ChatWidget";
import VehicleExtras from "@/components/VehicleExtras";
import ReminderOptIn from "@/components/ReminderOptIn";
import ReferralCard from "@/components/ReferralCard";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

function Dashboard({ user, setUser, onLogout }) {
  const [registration, setRegistration] = useState("");
  const [loading, setLoading] = useState(false);
  const [vehicleData, setVehicleData] = useState(null);
  const [error, setError] = useState(null);
  const [upgradingLoading, setUpgradingLoading] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('annual'); // 'monthly' or 'annual'
  const [imageFeedback, setImageFeedback] = useState(null); // 'positive' or 'negative'
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const formatRegistration = (value) => {
    return value.replace(/\s/g, "").toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${year} ${hours}:${minutes}`;
  };

  // Fetch search history
  useEffect(() => {
    fetchSearchHistory();
    
    // Check for shared registration in URL
    const urlParams = new URLSearchParams(window.location.search);
    const sharedReg = urlParams.get('reg');
    if (sharedReg) {
      setRegistration(sharedReg);
      // Auto-search after a short delay
      setTimeout(() => {
        lookupVehicle();
      }, 500);
      // Clean URL
      window.history.replaceState({}, document.title, '/dashboard');
    }
  }, []);

  const fetchSearchHistory = async () => {
    try {
      const response = await axios.get(`${API}/vehicle/history`, {
        withCredentials: true
      });
      setSearchHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  const deleteHistoryItem = async (lookupId, event) => {
    // Stop propagation so row click doesn't trigger
    event.stopPropagation();
    
    if (!window.confirm("Delete this search from history?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/vehicle/history/${lookupId}`, {
        withCredentials: true
      });
      
      // Remove from local state
      setSearchHistory(searchHistory.filter(item => item.id !== lookupId));
      toast.success("History item deleted");
    } catch (error) {
      console.error("Error deleting history:", error);
      toast.error("Failed to delete history item");
    }
  };

  const shareVehicle = (vehicle) => {
    const url = `${window.location.origin}?reg=${vehicle.registration}`;
    const text = `Check out this ${vehicle.year_of_manufacture} ${vehicle.make} ${vehicle.model || ''} on Is It Taxed - Tax: ${vehicle.tax_status}, MOT: ${vehicle.mot_status}`;
    
    // Try native share API first (mobile)
    if (navigator.share) {
      navigator.share({
        title: `${vehicle.registration} - Is It Taxed`,
        text: text,
        url: url
      }).catch(err => console.log('Share cancelled'));
    } else {
      // Show share menu for desktop
      setShareMenuOpen(!shareMenuOpen);
    }
  };

  const copyShareLink = (vehicle) => {
    const url = getShareUrl(vehicle);
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const getShareUrl = (vehicle) => {
    // Use share token if available (premium users get this)
    if (vehicle.share_token) {
      return `${window.location.origin}/shared/${vehicle.share_token}`;
    }
    // Fallback to registration
    return `${window.location.origin}?reg=${vehicle.registration}`;
  };

  const shareToWhatsApp = (vehicle) => {
    try {
      const url = getShareUrl(vehicle);
      const text = `Check out this ${vehicle.year_of_manufacture} ${vehicle.make} ${vehicle.model || ''} - Tax: ${vehicle.tax_status}, MOT: ${vehicle.mot_status}`;
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
      const popup = window.open(whatsappUrl, '_blank');
      
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        toast.error('Popup blocked! Please allow popups for this site.');
      } else {
        toast.success('Opening WhatsApp...');
      }
    } catch (error) {
      console.error('WhatsApp share error:', error);
      toast.error('Failed to open WhatsApp. Please check your browser settings.');
    }
  };

  const shareToFacebook = (vehicle) => {
    try {
      const url = getShareUrl(vehicle);
      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
      const popup = window.open(facebookUrl, '_blank', 'width=600,height=400');
      
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        toast.error('Popup blocked! Please allow popups for this site.');
      } else {
        toast.success('Opening Facebook...');
      }
    } catch (error) {
      console.error('Facebook share error:', error);
      toast.error('Failed to open Facebook. Please check your browser settings.');
    }
  };

  const shareToTwitter = (vehicle) => {
    try {
      const url = getShareUrl(vehicle);
      const text = `${vehicle.registration} - Tax: ${vehicle.tax_status}, MOT: ${vehicle.mot_status}`;
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
      const popup = window.open(twitterUrl, '_blank', 'width=600,height=400');
      
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        toast.error('Popup blocked! Please allow popups for this site.');
      } else {
        toast.success('Opening Twitter...');
      }
    } catch (error) {
      console.error('Twitter share error:', error);
      toast.error('Failed to open Twitter. Please check your browser settings.');
    }
  };

  const shareToEmail = (vehicle) => {
    const url = getShareUrl(vehicle);
    const subject = `Vehicle Check: ${vehicle.registration}`;
    const body = `Check out this ${vehicle.year_of_manufacture} ${vehicle.make} ${vehicle.model || ''}\n\nTax Status: ${vehicle.tax_status}\nMOT Status: ${vehicle.mot_status}\n\nView full details: ${url}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };


  const handleCameraDetect = (detectedPlate) => {
    setRegistration(detectedPlate);
    setShowCameraScanner(false);
    toast.success(`Number plate detected: ${detectedPlate}`);
  };

  const submitImageFeedback = async (feedback) => {
    if (!vehicleData || !vehicleData.image_url) return;
    
    try {
      await axios.post(`${API}/vehicle/image-feedback`, null, {
        params: {
          registration: vehicleData.registration,
          image_url: vehicleData.image_url,
          feedback: feedback,
          make: vehicleData.make,
          model: vehicleData.model
        },
        withCredentials: true
      });
      
      setImageFeedback(feedback);
      toast.success(feedback === 'positive' ? 'Thanks! Glad it matched!' : 'Thanks! We\'ll work on improving this.');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setRegistration(formatRegistration(value));
  };

  const isExpiringSoon = (dateString) => {
    if (!dateString) return false;
    const expiryDate = new Date(dateString);
    const today = new Date();
    const sixWeeksFromNow = new Date();
    sixWeeksFromNow.setDate(today.getDate() + 42);
    
    return expiryDate > today && expiryDate <= sixWeeksFromNow;
  };

  const isExpired = (dateString) => {
    if (!dateString) return false;
    const expiryDate = new Date(dateString);
    const today = new Date();
    return expiryDate < today;
  };

  const getStatusColor = (status, dateString) => {
    if (status && status.toUpperCase() === "SORN") {
      return "bg-red-50 border-red-200";
    }
    
    if (isExpired(dateString)) {
      return "bg-red-50 border-red-200";
    }
    
    if (isExpiringSoon(dateString)) {
      return "bg-yellow-50 border-yellow-200";
    }
    
    return "bg-white border-gray-200";
  };

  const lookupVehicle = async () => {
    if (!registration || registration.length < 2) {
      toast.error("Please enter a valid registration number");
      return;
    }

    setLoading(true);
    setError(null);
    setVehicleData(null);

    try {
      const response = await axios.post(`${API}/vehicle/lookup`, {
        registration: registration,
      }, {
        withCredentials: true
      });
      setVehicleData(response.data);
      setImageFeedback(null); // Reset feedback for new search
      toast.success("Vehicle found!");
      
      // Refresh user data to update check count
      const userResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(userResponse.data);
      
      // Refresh history
      fetchSearchHistory();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to fetch vehicle data";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      lookupVehicle();
    }
  };

  const handleUpgrade = async (period = billingPeriod) => {
    setUpgradingLoading(true);
    try {
      const response = await axios.post(`${API}/payments/create-checkout-session`, {
        billing_period: period
      }, {
        withCredentials: true
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Failed to start checkout");
      setUpgradingLoading(false);
    }
  };

  const isPremium = user.subscription_status === "premium";
  const checksRemaining = user.checks_remaining || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header - Golden for Premium */}
      <header className={`border-b shadow-sm ${
        isPremium 
          ? 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_carinfo-lookup/artifacts/kwmtzzvb_image.png" 
                alt="IsItTaxed.co.uk" 
                className="h-12 w-auto cursor-pointer"
                style={{ objectFit: 'contain' }}
                onClick={() => window.location.href = '/'}
              />
              {/* Premium Badge */}
              {isPremium && (
                <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                  <Crown className="h-4 w-4 text-white" />
                  <span className="text-sm font-bold text-white">Premium</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              {/* Widget Button */}
              <Button 
                onClick={() => window.location.href = '/widget'}
                variant="outline"
                size="sm"
                className={`hidden md:flex ${
                  isPremium 
                    ? 'bg-white/20 border-white/40 text-white hover:bg-white/30' 
                    : 'bg-green-50 border-green-200 hover:bg-green-100'
                }`}
              >
                🔧 Get Widget
              </Button>
              {/* Admin Button - Only visible to admin */}
              {user.email === "jamessnking@gmail.com" && (
                <Button 
                  onClick={() => window.location.href = '/admin'}
                  variant="outline"
                  size="sm"
                  className={`${
                    isPremium 
                      ? 'bg-white/20 border-white/40 text-white hover:bg-white/30' 
                      : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                  }`}
                >
                  📊 Analytics
                </Button>
              )}
              {/* Reminders Button - Premium+ only */}
              {isPremium && (
                <Button 
                  onClick={() => window.location.href = '/reminders'}
                  variant="outline"
                  size="sm"
                  className="bg-white/20 border-white/40 text-white hover:bg-white/30"
                >
                  <Bell className="h-4 w-4 mr-1" />
                  Reminders
                </Button>
              )}
              {/* User Info */}
              <div className="flex items-center gap-2">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full ring-2 ring-white/50" />
                ) : (
                  <User className={`h-8 w-8 ${isPremium ? 'text-white' : 'text-gray-600'}`} />
                )}
                <span className={`text-sm hidden md:block ${isPremium ? 'text-white font-medium' : 'text-gray-700'}`}>{user.name}</span>
              </div>
              <Button 
                onClick={onLogout}
                variant="outline"
                size="sm"
                data-testid="logout-btn"
                className={`${
                  isPremium 
                    ? 'bg-white/20 border-white/40 text-white hover:bg-white/30' 
                    : ''
                }`}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Free Plan Info - Only show for non-premium */}
        {!isPremium && (
          <Card className="mb-8 border-2 bg-gradient-to-r from-purple-50 to-pink-50">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Car className="h-6 w-6 text-gray-600" />
                    <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Free Plan</h2>
                  </div>
                  <p className="text-gray-600 mb-4">
                    {checksRemaining} checks remaining today • History limited to 10
                  </p>
                  <p className="text-sm text-gray-700 font-semibold">
                    Upgrade to Premium for complete vehicle data
                  </p>
                </div>

                {/* Billing Period Toggle */}
                <div className="flex items-center gap-2 bg-white rounded-lg p-1 border-2 border-gray-200">
                  <button
                    onClick={() => setBillingPeriod('monthly')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      billingPeriod === 'monthly'
                        ? 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingPeriod('annual')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      billingPeriod === 'annual'
                        ? 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Annual
                    <span className="ml-1 text-xs font-bold">SAVE 17%</span>
                  </button>
                </div>

                {/* Pricing Display */}
                <div className="text-center">
                  {billingPeriod === 'monthly' ? (
                    <div>
                      <div className="text-4xl font-bold text-gray-900">£4.99</div>
                      <div className="text-sm text-gray-600">per month</div>
                      <div className="text-xs text-gray-500 mt-1">£59.88 billed annually</div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-4xl font-bold text-gray-900">£49</div>
                      <div className="text-sm text-gray-600">per year</div>
                      <div className="text-xs text-green-600 font-semibold mt-1">Save £10.88 (2 months free!)</div>
                    </div>
                  )}
                </div>

                {/* Upgrade Button */}
                <Button 
                  onClick={() => handleUpgrade(billingPeriod)}
                  disabled={upgradingLoading}
                  size="lg"
                  className="bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white shadow-lg w-full max-w-xs"
                  data-testid="upgrade-btn"
                >
                  {upgradingLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Crown className="mr-2 h-5 w-5" />
                      Upgrade to Premium
                    </>
                  )}
                </Button>

                {/* Features List */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700 w-full max-w-md">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Unlimited searches</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Complete model data</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Full search history</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>CSV export</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search Section */}
        <div className={`mb-8 transition-all duration-500 ${vehicleData ? 'mb-4' : ''}`}>
          <div className={`text-center transition-all duration-500 ${vehicleData ? 'mb-3' : 'mb-6'}`}>
            <h2 className={`font-semibold text-gray-800 transition-all duration-500 ${vehicleData ? 'text-lg' : 'text-2xl'}`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {vehicleData ? 'Search Again' : 'Check Your Vehicle'}
            </h2>
            {!vehicleData && (
              <p className="text-gray-600">Enter your UK registration to view tax and MOT status</p>
            )}
          </div>

          {/* UK Number Plate Input */}
          <div className={`mx-auto transition-all duration-500 ${vehicleData ? 'w-full max-w-lg' : 'w-full'}`} style={{ maxWidth: vehicleData ? '512px' : '90%' }}>
            <div className="uk-plate-container" data-testid="registration-input-container">
              <div className={`uk-plate transition-all duration-500 ${vehicleData ? 'uk-plate-compact' : ''} relative`}>
                <input
                  data-testid="registration-input"
                  type="text"
                  value={registration}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder=""
                  maxLength="8"
                  className={`uk-plate-input transition-all duration-500 ${vehicleData ? 'uk-plate-input-compact' : ''}`}
                />
                {/* Camera Icon - Hidden temporarily until professional OCR implemented */}
                {/* {isMobile && (
                  <button
                    onClick={() => setShowCameraScanner(true)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-900 hover:bg-gray-800 rounded-full p-2 shadow-md transition-all"
                    title="Scan number plate"
                  >
                    <Camera className="h-5 w-5 text-yellow-400" />
                  </button>
                )} */}
              </div>
            </div>
            <div className={`mx-auto transition-all duration-500 ${vehicleData ? 'max-w-xs' : 'max-w-md'}`}>
              <Button
                data-testid="lookup-button"
                onClick={lookupVehicle}
                disabled={loading || (!isPremium && checksRemaining === 0)}
                className={`w-full mt-4 bg-gray-900 hover:bg-gray-800 text-white font-semibold transition-all duration-500 ${vehicleData ? 'h-10 text-sm' : 'h-12 text-base'}`}
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                {loading ? (
                  <>
                    <Loader2 className={`mr-2 animate-spin ${vehicleData ? 'h-4 w-4' : 'h-5 w-5'}`} />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className={`mr-2 ${vehicleData ? 'h-4 w-4' : 'h-5 w-5'}`} />
                    Check Vehicle
                  </>
                )}
              </Button>
              {!isPremium && checksRemaining === 0 && (
                <p className="text-center text-red-600 text-sm mt-2">
                  Daily limit reached. Upgrade to Premium for unlimited checks.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div data-testid="error-message" className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
            {error}
          </div>
        )}

        {/* Vehicle Results */}
        {vehicleData && (
          <div data-testid="vehicle-results" className="space-y-6 animate-in fade-in duration-500">
            {/* Car Image */}
            <Card className="overflow-hidden border-2">
              <CardContent className="p-0">
                {vehicleData.image_url ? (
                  <>
                    <img 
                      src={vehicleData.image_url} 
                      alt={`${vehicleData.make} ${vehicleData.model}`}
                      className="w-full h-64 md:h-96 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.querySelector('.no-image-fallback').style.display = 'flex';
                      }}
                    />
                    <div className="no-image-fallback hidden flex-col items-center justify-center h-64 md:h-96 bg-gray-100">
                      <Car className="h-16 w-16 text-gray-400 mb-3" />
                      <p className="text-gray-600 font-semibold">No image available</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {vehicleData.year_of_manufacture} {vehicleData.make} {vehicleData.model}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 md:h-96 bg-gray-100">
                    <Car className="h-16 w-16 text-gray-400 mb-3" />
                    <p className="text-gray-600 font-semibold">No image available</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {vehicleData.year_of_manufacture} {vehicleData.make} {vehicleData.model}
                    </p>
                  </div>
                )}
                <div className="p-4 bg-gray-50">
                  <div className="text-center mb-3">
                    <p className="text-sm text-gray-600">
                      {vehicleData.year_of_manufacture} {vehicleData.make} {vehicleData.model} - {vehicleData.colour}
                    </p>
                    {vehicleData.image_url && (
                      <>
                        <p className="text-xs text-gray-400 mt-1">Image for illustration purposes</p>
                        {vehicleData.image_color_match === false && (
                          <p className="text-xs text-orange-600 mt-1 font-semibold">
                            ⚠️ Color may differ from actual vehicle
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Image Feedback */}
                  {vehicleData.image_url && (
                    <div className="pt-2 border-t border-gray-200">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="text-xs text-gray-500">Does this image match the make & model?</span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => submitImageFeedback('positive')}
                            disabled={imageFeedback !== null}
                            className={`p-1.5 rounded-md transition-all ${
                              imageFeedback === 'positive'
                                ? 'bg-green-500 text-white'
                                : imageFeedback === 'negative'
                                ? 'opacity-30 cursor-not-allowed'
                                : 'hover:bg-green-100 text-green-600'
                            }`}
                            title="Good match"
                          >
                            <ThumbsUp className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => submitImageFeedback('negative')}
                            disabled={imageFeedback !== null}
                            className={`p-1.5 rounded-md transition-all ${
                              imageFeedback === 'negative'
                                ? 'bg-red-500 text-white'
                                : imageFeedback === 'positive'
                                ? 'opacity-30 cursor-not-allowed'
                                : 'hover:bg-red-100 text-red-600'
                            }`}
                            title="Wrong image"
                          >
                            <ThumbsDown className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 text-center">(Color may vary)</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Vehicle Details */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Vehicle Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DetailItem label="Registration" value={vehicleData.registration} testId="detail-registration" />
                  <DetailItem label="Make" value={vehicleData.make} testId="detail-make" />
                  <DetailItem label="Model" value={vehicleData.model} testId="detail-model" />
                  <DetailItem label="Colour" value={vehicleData.colour} testId="detail-colour" />
                  <DetailItem label="Fuel Type" value={vehicleData.fuel_type} testId="detail-fuel" />
                  <DetailItem label="Year" value={vehicleData.year_of_manufacture} testId="detail-year" />
                  {vehicleData.engine_capacity && (
                    <DetailItem label="Engine" value={`${vehicleData.engine_capacity}cc`} testId="detail-engine" />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Share & Reminder Section */}
            <Card className="border-2 bg-gradient-to-r from-blue-50 to-purple-50">
              <CardContent className="p-4">
                <div className="flex flex-col gap-4">
                  {/* Share Options */}
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <Share2 className="h-5 w-5 text-gray-700" />
                      <p className="text-sm font-semibold text-gray-700">Share this vehicle check</p>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <Button
                        onClick={() => shareToWhatsApp(vehicleData)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        WhatsApp
                      </Button>
                      <Button
                        onClick={() => shareToFacebook(vehicleData)}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Facebook
                      </Button>
                      <Button
                        onClick={() => shareToTwitter(vehicleData)}
                        size="sm"
                        className="bg-black hover:bg-gray-800 text-white"
                      >
                        Twitter
                      </Button>
                      <Button
                        onClick={() => shareToEmail(vehicleData)}
                        size="sm"
                        variant="outline"
                      >
                        Email
                      </Button>
                      <Button
                        onClick={() => copyShareLink(vehicleData)}
                        size="sm"
                        variant="outline"
                      >
                        Copy Link
                      </Button>
                    </div>
                  </div>
                  
                  {/* Quick Reminder Action - Premium Feature */}
                  {user?.subscription_status === 'premium' || user?.subscription_status === 'enterprise' ? (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-blue-200">
                      <div className="flex items-center gap-2">
                        <Bell className="h-5 w-5 text-amber-600" />
                        <p className="text-sm font-semibold text-gray-700">Get reminded before Tax/MOT expires</p>
                      </div>
                      <Button
                        onClick={() => {
                          const reminderSection = document.getElementById('reminder-section');
                          if (reminderSection) {
                            reminderSection.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                      >
                        <Bell className="h-4 w-4 mr-1" />
                        Set Reminder
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-blue-200">
                      <div className="flex items-center gap-2">
                        <Bell className="h-5 w-5 text-gray-400" />
                        <p className="text-sm text-gray-500">Get reminded before Tax/MOT expires</p>
                      </div>
                      <Button
                        onClick={() => {
                          const pricingSection = document.getElementById('pricing-section');
                          if (pricingSection) {
                            pricingSection.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                        size="sm"
                        variant="outline"
                        className="border-amber-400 text-amber-600 hover:bg-amber-50"
                      >
                        <Crown className="h-4 w-4 mr-1" />
                        Upgrade for Reminders
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tax and MOT Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Tax Status */}
              <Card className={`border-2 ${getStatusColor(vehicleData.tax_status, vehicleData.tax_due_date)}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {vehicleData.tax_status === "Taxed" && !isExpiringSoon(vehicleData.tax_due_date) ? (
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    ) : isExpiringSoon(vehicleData.tax_due_date) ? (
                      <CheckCircle className="h-6 w-6 text-yellow-600" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-600" />
                    )}
                    Tax Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div data-testid="tax-status">
                      <p className="text-sm text-gray-600 mb-1">Status</p>
                      <p className={`text-xl font-bold ${
                        vehicleData.tax_status && vehicleData.tax_status.toUpperCase() === "SORN"
                          ? "text-red-600"
                          : isExpired(vehicleData.tax_due_date)
                          ? "text-red-600"
                          : isExpiringSoon(vehicleData.tax_due_date)
                          ? "text-yellow-600"
                          : vehicleData.tax_status === "Taxed"
                          ? "text-green-600"
                          : "text-gray-600"
                      }`}>
                        {vehicleData.tax_status && vehicleData.tax_status.toUpperCase() === "SORN" 
                          ? "SORN" 
                          : vehicleData.tax_status || "Unknown"}
                      </p>
                      {isExpired(vehicleData.tax_due_date) && (
                        <p className="text-sm text-red-600 mt-1 font-semibold">⚠ Expired</p>
                      )}
                      {isExpiringSoon(vehicleData.tax_due_date) && !isExpired(vehicleData.tax_due_date) && (
                        <p className="text-sm text-yellow-600 mt-1 font-semibold">⚠ Expiring Soon</p>
                      )}
                    </div>
                    {vehicleData.tax_due_date && (
                      <div data-testid="tax-due-date">
                        <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Due Date
                        </p>
                        <p className="text-lg font-semibold">{formatDate(vehicleData.tax_due_date)}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* MOT Status */}
              <Card className={`border-2 ${getStatusColor(vehicleData.mot_status, vehicleData.mot_expiry_date)}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {vehicleData.mot_status === "Valid" && !isExpiringSoon(vehicleData.mot_expiry_date) ? (
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    ) : isExpiringSoon(vehicleData.mot_expiry_date) ? (
                      <CheckCircle className="h-6 w-6 text-yellow-600" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-600" />
                    )}
                    MOT Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div data-testid="mot-status">
                      <p className="text-sm text-gray-600 mb-1">Status</p>
                      <p className={`text-xl font-bold ${
                        isExpired(vehicleData.mot_expiry_date)
                          ? "text-red-600"
                          : isExpiringSoon(vehicleData.mot_expiry_date)
                          ? "text-yellow-600"
                          : vehicleData.mot_status === "Valid"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}>
                        {vehicleData.mot_status || "Unknown"}
                      </p>
                      {isExpired(vehicleData.mot_expiry_date) && (
                        <p className="text-sm text-red-600 mt-1 font-semibold">⚠ Expired</p>
                      )}
                      {isExpiringSoon(vehicleData.mot_expiry_date) && !isExpired(vehicleData.mot_expiry_date) && (
                        <p className="text-sm text-yellow-600 mt-1 font-semibold">⚠ Expiring Soon</p>
                      )}
                    </div>
                    {vehicleData.mot_expiry_date && (
                      <div data-testid="mot-expiry-date">
                        <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Expiry Date
                        </p>
                        <p className="text-lg font-semibold">{formatDate(vehicleData.mot_expiry_date)}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Vehicle Extras - Paid Add-ons */}
            <VehicleExtras 
              registration={vehicleData.registration} 
              user={user}
              isPremium={user?.subscription_status}
            />

            {/* Reminder Opt-In */}
            <div id="reminder-section">
              <ReminderOptIn 
                vehicle={{
                  registration: vehicleData.registration,
                  make: vehicleData.make,
                  model: vehicleData.model,
                  colour: vehicleData.colour,
                  taxDueDate: vehicleData.tax_due_date,
                  motExpiryDate: vehicleData.mot_expiry_date
                }}
                user={user}
                isPremium={user?.subscription_status}
              />
            </div>
          </div>
        )}

        {/* Referral Card - shown to all logged-in users */}
        <div className="mt-8">
          <ReferralCard user={user} />
        </div>

        {/* Search History */}
        {searchHistory && searchHistory.length > 0 && (
          <Card className="mt-8 border-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    <History className="h-5 w-5" />
                    Search History
                    {!isPremium && (
                      <span className="text-sm font-normal text-gray-500">(Last 10 searches)</span>
                    )}
                  </CardTitle>
                  <p className="text-xs text-gray-500">History automatically deleted after 30 days</p>
                </div>
                <div className="flex gap-2">
                  {isPremium && searchHistory.length > 0 && (
                    <Button
                      onClick={() => {
                        // Export to CSV
                        const csv = [
                          ['Registration', 'Make', 'Model', 'Tax Status', 'MOT Status', 'Date Checked'].join(','),
                          ...searchHistory.map(h => 
                            [h.registration, h.make || 'N/A', h.model || 'N/A', h.tax_status, h.mot_status, formatDateTime(h.timestamp)].join(',')
                          )
                        ].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'vehicle-history.csv';
                        a.click();
                        toast.success("History exported!");
                      }}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  )}
                  <Button
                    onClick={() => setShowHistory(!showHistory)}
                    variant="outline"
                    size="sm"
                  >
                    {showHistory ? "Hide" : "Show"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            {showHistory && (
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 text-sm font-semibold">Registration</th>
                        <th className="text-left py-2 px-3 text-sm font-semibold hidden md:table-cell">Make</th>
                        <th className="text-left py-2 px-3 text-sm font-semibold">Tax</th>
                        <th className="text-left py-2 px-3 text-sm font-semibold">MOT</th>
                        <th className="text-left py-2 px-3 text-sm font-semibold hidden sm:table-cell">Date</th>
                        <th className="text-left py-2 px-3 text-sm font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isPremium ? searchHistory : searchHistory.slice(0, 10)).map((history, index) => (
                        <tr 
                          key={history.id || index} 
                          className="border-b hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            // Set the registration in the input
                            setRegistration(history.registration);
                            // Display the full vehicle data from history
                            setVehicleData({
                              registration: history.registration,
                              make: history.make,
                              model: history.model,
                              colour: history.colour,
                              tax_status: history.tax_status,
                              tax_due_date: history.tax_due_date,
                              mot_status: history.mot_status,
                              mot_expiry_date: history.mot_expiry_date,
                              fuel_type: history.fuel_type,
                              year_of_manufacture: history.year_of_manufacture,
                              engine_capacity: history.engine_capacity,
                              image_url: history.image_url,
                              image_color_match: history.image_color_match
                            });
                            // Scroll to results
                            window.scrollTo({ top: 300, behavior: 'smooth' });
                            toast.success("Loaded from history");
                          }}
                        >
                          <td className="py-2 px-3 text-sm font-semibold">{history.registration}</td>
                          <td className="py-2 px-3 text-sm hidden md:table-cell">{history.make || 'N/A'}</td>
                          <td className="py-2 px-3 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              history.tax_status === 'Taxed' 
                                ? 'bg-green-100 text-green-800' 
                                : history.tax_status === 'SORN'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {history.tax_status}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              history.mot_status === 'Valid' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {history.mot_status}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-sm text-gray-600 hidden sm:table-cell">
                            {formatDateTime(history.timestamp)}
                          </td>
                          <td className="py-2 px-3 text-sm">
                            <Button
                              onClick={(e) => deleteHistoryItem(history.id, e)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete from history"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!isPremium && searchHistory.length > 10 && (
                  <div className="mt-4 text-center p-4 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-sm text-gray-700 mb-2">
                      <Crown className="h-4 w-4 inline text-yellow-600 mr-1" />
                      Upgrade to Premium to view unlimited history and export to CSV
                    </p>
                    <Button
                      onClick={handleUpgrade}
                      size="sm"
                      className="bg-yellow-500 hover:bg-yellow-600"
                    >
                      Upgrade Now
                    </Button>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-16 py-8 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4 text-center text-gray-600 text-sm">
          <p>Data provided by DVLA Vehicle Enquiry Service</p>
        </div>
      </footer>

      {/* Camera Scanner Modal */}
      <CameraScanner
        isOpen={showCameraScanner}
        onClose={() => setShowCameraScanner(false)}
        onDetect={handleCameraDetect}
      />

      {/* AI Chat Widget */}
      <ChatWidget />
    </div>
  );
}

const DetailItem = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <p className="text-sm text-gray-600 mb-1">{label}</p>
    <p className="text-lg font-semibold text-gray-900">{value || "N/A"}</p>
  </div>
);

function DashboardWithFooter(props) {
  return (
    <>
      <Dashboard {...props} />
      <Footer />
    </>
  );
}

export default DashboardWithFooter;
